import React, {FC, useEffect, useMemo, useState} from "react";
import BTable from "../../../components/b-table";
import {formatNumber, getFiat, toNumber, toXDCNumber} from "../../../common/utils/function";
import useCommon from "../../../common/hooks/useCommon";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import TokenImage from "../../common/token-image";

interface MultichainPortfolioListProps {
    selectedChain: string[];
    totalChains: number;
    totalValueselected: number;
    totalValue: number;
    chainTokenInfo: Object[];
}

interface MultichainPortfolioListData {
    chain: string;
    token: string;
    portfolio: { value: string };
    price: { value: string };
    amount: { value: string };
    value: string;
    image: string;
}

const MultichainPortfolioList: FC<MultichainPortfolioListProps> = (props) => {
    const selectedChainCount = props.selectedChain.length;
    const selectedChain = props.selectedChain;
    const totalChainscount = props.totalChains;
    const totalValueselectedcount = props.totalValueselected;
    const fiat = useSelector((e: Object) => (e as any)?.common?.fiat);
    const totalValuecount = props.totalValue;
    const allChainTokenData = props.chainTokenInfo;
    const {getXDC} = useCommon();
    const {t, i18n} = useTranslation();
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [randomValue, setRandomValue] = useState<number>(Math.random());
    const [multichainportfolioList, setMultichainPortfolioList] = useState<any[]>([]);

    useEffect(() => {
        let filteredList = allChainTokenData;
        if (props.selectedChain.length > 0) {
            filteredList = filteredList.filter((item: any) =>
                props.selectedChain.includes(item.Name)
            );
            const addTokenList = filteredList.map((item: any) => {
                if (item?.tokenList?.items) {
                    return item?.tokenList?.items.map((ele: any) => {
                        return {
                            ...ele,
                            ...item,
                        };
                    });
                }
            });
            const filterParentList = filteredList
                .filter((item: any) => item.balance)
                .map((item: any) => {
                    return {
                        ...item,
                        value: item.balance,
                        exchange_rate: item.exchange_rate,
                        token: {
                            decimals: null,
                            fiat_value: item.fiat_value,
                            exchange_rate: item.exchange_rate,
                            address: item.hash,
                        },
                    };
                });
            setMultichainPortfolioList(
                addTokenList
                    ?.flat()
                    .filter((item: any) => item !== undefined)
                    .concat(filterParentList)
            );
            setRandomValue(Math.random());
        }
    }, [props.chainTokenInfo, props.selectedChain]);

    const totalPortfolioValue = useMemo(() => {
        return multichainportfolioList.reduce((total, item) => {
            const tokenExchangeRate = item?.token?.exchange_rate || 0;
            const tokenValue = item?.token?.value || 0;
            const exchangeRate = item?.token?.exchange_rate || (item?.token?.exchange_rate ? item.exchange_rate : "0.00");
            const value = item.value || 0;

            const itemValue = (tokenExchangeRate * tokenValue) + (exchangeRate * value);
            return total + itemValue;
        }, 0);
    }, [multichainportfolioList]);

    const calculatePortfolioPercentage = (data: any) => {
        const tokenExchangeRate = data?.token?.exchange_rate || 0;
        const tokenValue = data?.token?.value || 0;
        const exchangeRate = data?.token?.exchange_rate || (data?.token?.exchange_rate ? data.exchange_rate : "0.00");
        const value = data.value || 0;

        const itemValue = (tokenExchangeRate * tokenValue) + (exchangeRate * value);
        return ((itemValue / totalPortfolioValue) * 100).toFixed(2);
    };

    const header = useMemo(() => {
        return [
            {
                title: `${t("Chain")}`,
                param: "Name",
                tooltip: false,
                render: (data: any) => (
                    <React.Fragment>
                        <img
                            src={`${process.env.REACT_APP_BASE_URL}/tokens/img/${data?.Symbol}.png`}
                            alt="OpenScan.ai"
                            className="xdc-nav__logo logo-light"
                            style={{width: "26px", height: "26px"}}
                        />
                        &nbsp;{data?.Name}
                    </React.Fragment>
                ),
            },
            {
                title: `${t("Token")}`,
                param: "token",
                render: (data: any) => (
                    <p className="text-truncate">
                        <a
                            href={`${data?.Explorer_link}token/${data?.token?.address}`}
                            target="_blank"
                            className="d-flex align-items-center"
                        >
                            <TokenImage image={data?.token?.icon_url} symbol={data?.token?.symbol || data?.Symbol}
                                        params={{width: 26, height: 26, className: "bs-icon-avatar mr-2"}}/>&nbsp;

                            {data?.token?.name || data?.Name} ({data?.token?.symbol || data?.Symbol})

                        </a>
                    </p>
                ),
            },
            {
                title: `${t("Portfolio %")}`,
                param: "value",
                tooltip: false,
                render: (data: any) => {
                    const percentage = calculatePortfolioPercentage(data);
                    return `${percentage}%`;
                },
            },
            {
                title: `${t("Price")}`,
                param: "value",
                tooltip: false,
                render: (data: any) => {
                    const exchangeRate = data?.token?.exchange_rate;
                    const formattedPrice = exchangeRate !== null && exchangeRate !== undefined ? getFiat(exchangeRate, 6) : "$0.00";
                    return <React.Fragment>{formattedPrice}</React.Fragment>;
                },
            },
            {
                title: `${t("Amount")}`,
                param: "value",
                tooltip: false,
                render: (data: any) => {
                    return (
                        <React.Fragment>
                            {formatNumber(toNumber(data?.value).toString().substring(0, 12))}
                        </React.Fragment>
                    );
                },
            },
            {
                title: `${t("Value")}`,
                param: "value",
                tooltip: false,
                render: (data: any) => {
                    const exchangeRate = data?.token?.exchange_rate || (data?.token?.exchange_rate ? data.exchange_rate : "0.00")
                    return getFiat(
                        toXDCNumber(exchangeRate * Number(data?.value)), 0, fiat, true
                    );
                },
            },
        ];
    }, [multichainportfolioList, totalPortfolioValue]);

    // Add pagination functionality
    const [loading, setLoading] = useState(true);
    const [pageIndex, setPageIndex] = useState(0);

    const fetchData = () => {
        setTimeout(() => {
            setLoading(false);
        }, 1000);
    };

    useEffect(() => {
        setLoading(true);
        fetchData();
    }, [pageIndex]);

    return (
        <div>
            <div className="bs-card-body">
                <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center pt-1 pb-2 px-4 text-muted">
                    <div>
                        {t("Showing")}
                        <span className="fw-medium">
                            {" "}
                            {multichainportfolioList.length}
                        </span>{" "}
                        {t("tokens with a value of")}{" "}
                        <span className="fw-medium">
                            {" "}
                            {totalValueselectedcount
                                ? getFiat(totalValueselectedcount, 2)
                                : 0}
                        </span>
                    </div>
                    <div>
                        <div id="js-chain-table_filter" className="dataTables_filter">
                            <label className="position-relative">
                                <span
                                    className="d-flex align-items-center position-absolute top-0 bottom-0"
                                    style={{left: "0.5rem"}}
                                >
                                    <i className="bi bi-search fs-sm text-muted"></i>
                                </span>
                                <input
                                    type="search"
                                    className="form-control form-control-sm datatable-form-control bg-light bg-focus-white py-1.5"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search"
                                    aria-controls="js-chain-table"
                                />
                            </label>
                        </div>
                    </div>
                </div>
            </div>
            <div className="bs-multichain-portfolio-custom-btable">
                <BTable
                    title=""
                    key={randomValue}
                    header={header}
                    topHeaderPagination={false}
                    list={multichainportfolioList}
                    loading={loading}
                    pageIndex={pageIndex}
                    setPageIndex={setPageIndex}
                />
            </div>
        </div>
    );
};

export default MultichainPortfolioList;
